import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { useTheme } from './useTheme';

const CompanyContext = createContext();

export { CompanyContext };

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [companyData, setCompanyData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  // Apply theme when company data changes
  useTheme(companyData?.settings?.customization);

  // Fetch company data and usage
  const fetchCompanyData = useCallback(async () => {
    if (!isAuthenticated || !user?.company) return;

    try {
      setLoading(true);
      
      // Fetch company data based on user role
      const hasAdminAccess = user.role === 'super_admin' || user.role === 'company_admin';
      
      if (hasAdminAccess) {
        // Fetch full company details, usage, and dashboard data for admins
        const [companyResponse, usageResponse, dashboardResponse] = await Promise.all([
          api.get(`/companies/${user.company._id}`),
          api.get('/dashboard/company-usage'),
          api.get('/dashboard/company-admin')
        ]);

        // Merge company data with dashboard data to include subscription status
        setCompanyData(prevData => ({
          ...companyResponse.data,
          subscriptionStatus: dashboardResponse.data.company.subscriptionStatus || prevData?.subscriptionStatus,
          subscriptionEndDate: dashboardResponse.data.company.subscriptionEndDate || prevData?.subscriptionEndDate
        }));
        setUsage({
          ...usageResponse.data.usage,
          limits: usageResponse.data.limits,
          quotaStatus: usageResponse.data.quotaStatus
        });
      } else {
        // For regular users, fetch basic company info
        const companyResponse = await api.get(`/companies/${user.company._id}`);
        setCompanyData(companyResponse.data);
        setUsage(null);
      }
    } catch (error) {
      console.error('Failed to fetch company data:', error);
      // For regular users, this is expected - set basic company info
      if (user?.company) {
        setCompanyData(user.company);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Initialize company data
  useEffect(() => {
    if (isAuthenticated && user?.company && !companyData?.subscriptionStatus) {
      fetchCompanyData();
    }
  }, [isAuthenticated, user, fetchCompanyData, companyData?.subscriptionStatus]);

  // Refresh usage data
  const refreshUsage = async () => {
    if (!isAuthenticated || !user?.company) return;

    const hasAdminAccess = user.role === 'super_admin' || user.role === 'company_admin';
    if (!hasAdminAccess) return;

    try {
      const response = await api.get('/dashboard/company-usage');
      setUsage({
        ...response.data.usage,
        limits: response.data.limits,
        quotaStatus: response.data.quotaStatus
      });
    } catch (error) {
      console.error('Failed to refresh usage data:', error);
    }
  };

  // Check if storage limit exceeded
  const isStorageLimitExceeded = () => {
    return usage?.quotaStatus?.storageExceeded || false;
  };

  // Get storage usage percentage
  const getStorageUsagePercentage = () => {
    return usage?.quotaStatus?.storagePercentage || 0;
  };

  // Check if user limit exceeded
  const isUserLimitExceeded = () => {
    return usage?.quotaStatus?.usersExceeded || false;
  };

  // Check if leads limit exceeded
  const isLeadsLimitExceeded = () => {
    if (!usage || !companyData?.plan) return false;
    return usage.leadsCount > companyData.plan.maxLeads;
  };

  // Check if customers limit exceeded
  const isCustomersLimitExceeded = () => {
    if (!usage || !companyData?.plan) return false;
    return usage.customersCount > companyData.plan.maxCustomers;
  };

  // Get remaining storage in MB
  const getRemainingStorage = () => {
    if (!usage || !companyData?.plan) return 0;
    return Math.max(companyData.plan.storageLimitMB - usage.storageUsedMB, 0);
  };

  // Format storage size
  const formatStorageSize = (sizeInMB) => {
    const sizeInBytes = sizeInMB * 1024 * 1024; // Convert MB to bytes

    if (sizeInBytes >= 1024 * 1024 * 1024) { // >= 1 GB
      return `${(sizeInBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    } else if (sizeInBytes >= 1024 * 1024) { // >= 1 MB
      return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    } else if (sizeInBytes >= 1024) { // >= 1 KB
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    } else {
      return `${sizeInBytes.toFixed(0)} B`;
    }
  };

  // Get subscription status
  const getSubscriptionStatus = () => {
    if (!companyData) return 'unknown';

    const now = new Date();
    const endDate = new Date(companyData.subscriptionEndDate);

    // First check if subscription has expired (regardless of status)
    if (endDate < now) {
      return 'expired';
    }

    if (companyData.subscriptionStatus === 'trial') {
      return 'trial';
    }

    if (companyData.subscriptionStatus === 'cancelled') {
      return 'cancelled';
    }

    // Check if expiring soon (within 7 days)
    const daysUntilExpiry = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    if (daysUntilExpiry <= 7) {
      return 'expiring-soon';
    }

    return 'active';
  };

  // Get days until subscription expires
  const getDaysUntilExpiry = () => {
    if (!companyData?.subscriptionEndDate) return null;
    
    const now = new Date();
    const endDate = new Date(companyData.subscriptionEndDate);
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Check if company can perform action (not in read-only mode)
  const canPerformAction = () => {
    const status = getSubscriptionStatus();
    return !isStorageLimitExceeded() && (status === 'active' || status === 'trial');
  };

  const value = {
    companyData,
    setCompanyData,
    usage,
    setUsage,
    loading,
    fetchCompanyData,
    refreshUsage,
    isStorageLimitExceeded,
    getStorageUsagePercentage,
    isUserLimitExceeded,
    isLeadsLimitExceeded,
    isCustomersLimitExceeded,
    getRemainingStorage,
    formatStorageSize,
    getSubscriptionStatus,
    getDaysUntilExpiry,
    canPerformAction
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};
